// ── Sprint Planning: two-panel drag layout with capacity tracking ──

import { api, getSprintVelocity } from '../api.js';
import { checkAuth, getUser } from '../auth.js';
import { showToast, openModal, closeModal } from '../ui.js';
import { escapeHtml, getTypeIcon, debounce } from '../utils.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { openTaskDetail, closeTaskDetail } from '../components/task-detail.js';
import { findInsertBefore, dropIndex } from '../lib/drag-order.js';
import { loadProject } from '../lib/project.js';
import { can } from '../lib/permissions.js';
import { groupLabelPlural, groupLabelSingular, isGeneratedGroupName } from '../lib/task-group-label.js';
import { parseDayInput } from '../lib/due-tasks.js';
import { sprintSchedule, scheduleSummary, sprintDateErrorText } from '../lib/sprint-dates.js';
import { loadStatuses } from '../lib/statuses.js';
import { sprintPhase, phaseLabel, nextOpenSprint, carryOverSummary, itemCount, lifecycleErrorText } from '../lib/sprint-lifecycle.js';
import { VELOCITY_WINDOW, rollingVelocity, formatValue } from '../lib/burndown-chart.js';
import { parseVelocityOverride, sprintCapacity, renderCapacityIndicator, averageSourceText } from '../lib/sprint-capacity.js';
import { getNavigationId } from '../router.js';

const DRAG_THRESHOLD = 5;
const VELOCITY_KEY = 'tipatask_velocity_';

let state = {
  projectId: null,
  project: null,
  tasks: [],
  sprints: [],
  selectedSprintId: null,
  searchQuery: '',
  drag: null,
  // Owner tokens of the drawer and modal this page opened (TPT647), passed to their closers on
  // teardown so a late teardown leaves another page's drawer/modal open.
  detailToken: null,
  modalToken: null,
  // Sprint Start/End saves of this mount (TPT589), chained in order; see queueSprintDatesSave().
  datesSave: Promise.resolve(),
  datesPending: 0,
  // A Start or Complete request of this mount is running (TPT9102); see lifecycleStep().
  lifecycleBusy: false,
  // The closed sprints' rolling average (TPT9103), rollingVelocity()'s result; see getVelocity().
  velocity: { average: null, count: 0 },
};

function resetState() {
  state = {
    projectId: null, project: null, tasks: [], sprints: [],
    selectedSprintId: null, searchQuery: '', drag: null,
    detailToken: null, modalToken: null,
    datesSave: Promise.resolve(), datesPending: 0,
    lifecycleBusy: false,
    velocity: { average: null, count: 0 },
  };
}

// (C1395) — this page never fetched the project before, so every group noun here was a
// hardcoded "Sprint" regardless of task_group_label. Mirrors board.js's own
// groupLabelSingular/Plural(state.project && state.project.task_group_label) call shape.
function singular() {
  return groupLabelSingular(state.project && state.project.task_group_label);
}
function plural() {
  return groupLabelPlural(state.project && state.project.task_group_label);
}
// This selector shows a sprint's raw name when it's genuinely custom (unlike
// sprint-board.js's lane titles, it never prefixes a custom name with the noun) — only
// a boilerplate auto-name ("Sprint N", written regardless of task_group_label) resolves
// to the live noun instead of echoing "Sprint" verbatim.
function selectorLabel(s) {
  return isGeneratedGroupName(s.name, s.number) ? `${singular()} ${s.number}` : s.name;
}

// ── Helpers ──

function getSelectedSprint() {
  return state.sprints.find(s => s.id === state.selectedSprintId) || null;
}

function getBacklogTasks() {
  let tasks = state.tasks.filter(t => t.priority === 0);
  if (state.searchQuery) {
    const q = state.searchQuery.toLowerCase();
    tasks = tasks.filter(t => t.title.toLowerCase().includes(q));
  }
  return tasks.sort((a, b) => b.display_order - a.display_order);
}

function getSprintTasks() {
  const sprint = getSelectedSprint();
  if (!sprint) return [];
  return state.tasks.filter(t => t.priority === sprint.number)
    .sort((a, b) => b.display_order - a.display_order);
}

// The velocity someone typed in this browser, which overrides the computed one until cleared.
function getVelocityOverride() {
  return parseVelocityOverride(localStorage.getItem(VELOCITY_KEY + state.projectId));
}

function setVelocityOverride(value) {
  const n = parseVelocityOverride(value);
  if (n == null) {
    localStorage.removeItem(VELOCITY_KEY + state.projectId);
  } else {
    localStorage.setItem(VELOCITY_KEY + state.projectId, n);
  }
}

// What a sprint is measured against: { points, source }, or null with no history and no override.
function getVelocity() {
  return sprintCapacity(getVelocityOverride(), state.velocity.average);
}

// Reads the rolling average. A failed read keeps the figure this page has: the indicator then shows
// what it showed, and with nothing to show it offers "Set velocity".
async function loadVelocity(owner) {
  try {
    const { sprints } = await getSprintVelocity(owner.projectId, VELOCITY_WINDOW);
    owner.velocity = rollingVelocity(sprints);
  } catch (_) {}
}

function sumPoints(tasks) {
  return tasks.reduce((s, t) => s + (t.story_points || 0), 0);
}

// Moving items, creating sprints and editing a goal all need task:write.
function canWrite() {
  return can(state.project, getUser(), 'task:write');
}

// ── Rendering ──

function renderPlanningItem(task) {
  const catClass = task.category === 'CODING' ? 'badge-coding' : 'badge-human';
  let html = `<div class="planning-item" data-task-key="${escapeHtml(task.task_key)}" data-priority="${task.priority}">`;
  if (canWrite()) html += `<span class="planning-drag-handle" title="Drag to move">&#10495;</span>`;
  html += getTypeIcon(task.type);
  html += `<a class="badge ${catClass} task-key-link" style="flex-shrink:0;" href="#/projects/${state.projectId}/tasks/${encodeURIComponent(task.task_key)}" draggable="false" title="Open task page">${escapeHtml(task.task_key)}</a>`;
  html += `<span class="planning-item-title">${escapeHtml(task.title)}</span>`;
  if (task.story_points != null) {
    html += `<span class="backlog-sp-pill">${task.story_points}</span>`;
  }
  html += `</div>`;
  return html;
}

function renderLeftPanel() {
  const tasks = getBacklogTasks();
  const pts = sumPoints(tasks);

  let html = `<div class="planning-panel" id="planning-left">`;
  html += `<div class="planning-panel-header"><h3>Product Backlog</h3><span class="planning-count">${tasks.length} item${tasks.length !== 1 ? 's' : ''}</span></div>`;
  html += `<input type="text" class="form-input planning-search" id="planning-search" placeholder="Search backlog..." value="${escapeHtml(state.searchQuery)}">`;
  html += `<div class="planning-items" id="backlog-items" data-panel="backlog">`;
  if (tasks.length === 0) {
    html += `<div class="empty text-muted" style="padding:1rem;font-size:0.85rem;">${state.searchQuery ? 'No matching items' : 'No backlog items'}</div>`;
  } else {
    html += tasks.map(renderPlanningItem).join('');
  }
  html += `</div>`;
  html += `<div class="planning-footer"><span>${pts} pts</span></div>`;
  html += `</div>`;
  return html;
}

function renderSprintSelector() {
  const active = state.sprints.filter(s => s.status === 'active');
  const closed = state.sprints.filter(s => s.status === 'closed');

  let html = `<select class="form-select" id="sprint-selector" style="flex:1;min-width:0;">`;
  if (!state.selectedSprintId && state.sprints.length > 0) {
    html += `<option value="" disabled selected>Select ${escapeHtml(singular().toLowerCase())}...</option>`;
  }
  if (!state.sprints.length) {
    html += `<option value="" disabled selected>No ${escapeHtml(plural().toLowerCase())}</option>`;
  }
  active.forEach(s => {
    const sel = s.id === state.selectedSprintId ? ' selected' : '';
    html += `<option value="${s.id}"${sel}>${escapeHtml(selectorLabel(s))}</option>`;
  });
  if (closed.length) {
    html += `<optgroup label="Closed">`;
    closed.forEach(s => {
      const sel = s.id === state.selectedSprintId ? ' selected' : '';
      html += `<option value="${s.id}"${sel}>${escapeHtml(selectorLabel(s))}</option>`;
    });
    html += `</optgroup>`;
  }
  if (canWrite()) html += `<option value="__new__">+ New ${escapeHtml(singular())}</option>`;
  html += `</select>`;
  return html;
}

// The selected sprint's Start/End dates (TPT589): a strip under the panel header's selector — this
// page shows one sprint at a time, so it is that sprint's header. A writer gets two date inputs,
// saved together by queueSprintDatesSave(); their min/max keep the picker from offering an end
// before the start, and a typed one comes back as the API's 400, shown in .planning-dates-error. A
// viewer gets the range as text, and nothing at all for a sprint without dates.
function renderSprintDates(sprint) {
  const start = sprint.start_date || '';
  const end = sprint.end_date || '';
  const schedule = sprintSchedule(start, end);
  if (!canWrite()) {
    if (!schedule) return '';
    const summary = scheduleSummary(schedule);
    return `<div class="planning-dates planning-dates--readonly"><span class="planning-dates-range">${escapeHtml(schedule.range)}</span>${summary ? `<span class="planning-dates-summary">${escapeHtml(summary)}</span>` : ''}</div>`;
  }
  let html = `<div class="planning-dates" data-sprint-id="${Number(sprint.id)}">`;
  html += `<label class="planning-date-field"><span class="planning-date-label">Start</span>`;
  html += `<input type="date" class="form-input planning-date-input" id="planning-start-date" data-field="start_date" value="${escapeHtml(start)}" max="${escapeHtml(end || '9999-12-31')}"></label>`;
  html += `<label class="planning-date-field"><span class="planning-date-label">End</span>`;
  html += `<input type="date" class="form-input planning-date-input" id="planning-end-date" data-field="end_date" value="${escapeHtml(end)}"${start ? ` min="${escapeHtml(start)}"` : ''} max="9999-12-31"></label>`;
  html += `<span class="planning-dates-summary">${escapeHtml(datesSummary(schedule))}</span>`;
  html += `<div class="planning-dates-error" role="alert" hidden></div>`;
  html += `</div>`;
  return html;
}

// The selected sprint's lifecycle (TPT9102), under its dates: the phase in words, and for a writer
// the one step it can take next. A closed sprint has none; reopening stays in the Manage dialog.
function renderSprintLifecycle(sprint) {
  const phase = sprintPhase(sprint);
  let html = `<div class="planning-lifecycle">`;
  html += `<span class="planning-lifecycle-state">${escapeHtml(phaseLabel(sprint))}</span>`;
  if (canWrite() && phase !== 'closed') {
    const started = phase === 'running';
    html += `<button type="button" class="btn btn-sm ${started ? 'btn-ghost' : 'btn-primary'}" id="${started ? 'planning-complete-sprint' : 'planning-start-sprint'}">`;
    html += `${started ? 'Complete' : 'Start'} ${escapeHtml(singular())}</button>`;
  }
  html += `</div>`;
  return html;
}

function datesSummary(schedule) {
  return schedule ? scheduleSummary(schedule) : `Set dates to time-box this ${singular().toLowerCase()}`;
}

function goalHtml(goal) {
  return goal ? escapeHtml(goal) : `<span class="text-faint">Click to set ${escapeHtml(singular().toLowerCase())} goal...</span>`;
}

function renderRightPanel() {
  const sprint = getSelectedSprint();
  const tasks = getSprintTasks();
  const pts = sumPoints(tasks);

  let html = `<div class="planning-panel" id="planning-right">`;
  html += `<div class="planning-panel-header">${renderSprintSelector()}</div>`;
  if (sprint) html += renderSprintDates(sprint);
  if (sprint) html += renderSprintLifecycle(sprint);

  // Goal
  if (sprint) {
    const goal = sprint.goal || '';
    if (canWrite()) {
      html += `<div class="planning-goal" id="planning-goal" title="Click to edit goal">${goalHtml(goal)}</div>`;
    } else if (goal) {
      html += `<div class="planning-goal planning-goal--readonly">${escapeHtml(goal)}</div>`;
    }
  }

  html += `<div class="planning-items" id="sprint-items" data-panel="sprint">`;
  if (!sprint) {
    html += `<div class="empty text-muted" style="padding:1rem;font-size:0.85rem;">Select a ${escapeHtml(singular().toLowerCase())}</div>`;
  } else if (tasks.length === 0) {
    html += `<div class="empty text-muted" style="padding:1rem;font-size:0.85rem;">Drag items here to commit</div>`;
  } else {
    html += tasks.map(renderPlanningItem).join('');
  }
  html += `</div>`;

  html += `<div class="planning-footer">`;
  html += `<span>${pts} pts committed</span>`;
  html += renderCapacityIndicator(pts, getVelocity(), { averageNote: averageSourceText(state.velocity.count, singular(), plural()) });
  html += `</div>`;
  html += `</div>`;
  return html;
}

function renderPlanning() {
  const container = document.getElementById('planning-container');
  if (!container) return;
  container.innerHTML = `<div class="planning-layout">${renderLeftPanel()}${renderRightPanel()}</div>`;
}

// ── Sprint actions ──

function handleSprintSelect(e) {
  const val = e.target.value;
  if (val === '__new__') {
    // Reset selector to previous
    const sel = document.getElementById('sprint-selector');
    if (sel) {
      const prev = state.selectedSprintId;
      const opt = sel.querySelector(`option[value="${prev}"]`);
      if (opt) opt.selected = true;
    }
    handleNewSprint();
    return;
  }
  state.selectedSprintId = parseInt(val, 10);
  renderPlanning();
}

function handleNewSprint() {
  const label = singular();
  const body = `
    <form id="new-sprint-form" style="display:flex;flex-direction:column;gap:0.75rem;">
      <input class="form-input" id="new-sprint-name" placeholder="${escapeHtml(label)} name" required maxlength="255" autofocus>
      <button type="submit" class="btn btn-primary">Create ${escapeHtml(label)}</button>
    </form>`;

  state.modalToken = openModal(`New ${label}`, body, {
    onMount(overlay) {
      overlay.querySelector('#new-sprint-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const input = overlay.querySelector('#new-sprint-name');
        const name = input.value.trim();
        if (!name) return;
        input.disabled = true;
        try {
          const { sprint } = await api.post('/projects/' + state.projectId + '/sprints', { name });
          state.sprints.push(sprint);
          state.selectedSprintId = sprint.id;
          closeModal();
          renderPlanning();
          showToast(`${label} created`, 'success');
        } catch (err) {
          showToast(err.message || `Failed to create ${label.toLowerCase()}`, 'error');
          input.disabled = false;
        }
      });
    },
  });
}

// Repaints only the goal line (TPT589): a full renderPlanning() would also rebuild the sprint's date
// inputs, and the click that ends a goal edit (its blur saves) may be landing on one of them.
function syncGoal() {
  const goalEl = document.getElementById('planning-goal');
  const sprint = getSelectedSprint();
  if (!goalEl || !sprint) {
    renderPlanning();
    return;
  }
  goalEl.innerHTML = goalHtml(sprint.goal || '');
}

async function handleGoalSave(newGoal) {
  const sprint = getSelectedSprint();
  if (!sprint) return;
  const old = sprint.goal;
  sprint.goal = newGoal;
  syncGoal();
  try {
    await api.patch('/projects/' + state.projectId + '/sprints/' + sprint.id, { goal: newGoal });
  } catch (err) {
    sprint.goal = old;
    syncGoal();
    showToast('Failed to update goal', 'error');
  }
}

// ── Sprint dates (TPT589) ──

function dateInputValue(input) {
  return input.validity && input.validity.badInput ? undefined : parseDayInput(input.value);
}

// The strip of `sprintId` this mount is showing, or null (another sprint selected, page left).
function liveDatesStrip(owner, sprintId) {
  if (state !== owner) return null;
  return document.querySelector(`#planning-container .planning-dates[data-sprint-id="${sprintId}"]`);
}

function showDatesError(strip, message) {
  const el = strip.querySelector('.planning-dates-error');
  if (el) {
    el.textContent = message;
    el.hidden = !message;
  }
  strip.querySelectorAll('.planning-date-input').forEach((input) => {
    if (message) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  });
}

// Brings `strip` in line with the sprint's saved dates: the inputs' coupled min/max, the summary,
// no error. `mirror` also writes the saved dates into inputs the user is not in — a renderPlanning()
// during the save rebuilt them from the row as it was before.
function syncDatesStrip(strip, sprint, mirror) {
  const startInput = strip.querySelector('[data-field="start_date"]');
  const endInput = strip.querySelector('[data-field="end_date"]');
  if (!startInput || !endInput) return;
  if (mirror) {
    if (startInput !== document.activeElement) startInput.value = sprint.start_date || '';
    if (endInput !== document.activeElement) endInput.value = sprint.end_date || '';
  }
  startInput.max = sprint.end_date || '9999-12-31';
  if (sprint.start_date) endInput.min = sprint.start_date;
  else endInput.removeAttribute('min');
  const summary = strip.querySelector('.planning-dates-summary');
  if (summary) summary.textContent = datesSummary(sprintSchedule(sprint.start_date, sprint.end_date));
  showDatesError(strip, '');
}

// Queues a save of the Start/End pair in `strip`. The sprint id and both inputs are taken now — by
// the time the job runs the strip on screen may belong to another sprint — and read when it runs,
// so the newest pair wins and the jobs behind it dedupe to nothing. Jobs chain per page mount so
// their PATCHes reach the API in order (Chrome fires `change` on every keystroke that completes a
// date). A teardown never drops one, only its DOM follow-up.
function queueSprintDatesSave(strip) {
  const owner = state;
  const job = {
    sprintId: Number(strip.dataset.sprintId),
    startInput: strip.querySelector('[data-field="start_date"]'),
    endInput: strip.querySelector('[data-field="end_date"]'),
  };
  if (!job.startInput || !job.endInput) return;
  owner.datesPending++;
  owner.datesSave = owner.datesSave
    .then(() => saveSprintDates(owner, job))
    .catch(() => {})
    .then(() => { owner.datesPending--; });
}

// Sends both dates, so the API checks the pair as typed rather than one side against the stored
// other one.
async function saveSprintDates(owner, { sprintId, startInput, endInput }) {
  const sprint = owner.sprints.find(s => s.id === sprintId);
  if (!sprint) return;
  const start = dateInputValue(startInput);
  const end = dateInputValue(endInput);
  if (start === undefined || end === undefined) return; // a date still being typed
  // Only the last queued job mirrors the saved dates into the inputs (see syncDatesStrip()).
  const last = () => owner.datesPending === 1;
  if (start === (sprint.start_date || null) && end === (sprint.end_date || null)) {
    // Back on the saved pair: nothing to send, and an earlier error no longer applies.
    const strip = liveDatesStrip(owner, sprintId);
    if (strip) syncDatesStrip(strip, sprint, last());
    return;
  }
  try {
    const { sprint: saved } = await api.patch(`/projects/${owner.projectId}/sprints/${sprintId}`, { start_date: start, end_date: end });
    // Only the dates — this reply's goal may be older than a goal saved meanwhile.
    sprint.start_date = saved.start_date ?? null;
    sprint.end_date = saved.end_date ?? null;
    const strip = liveDatesStrip(owner, sprintId);
    if (strip) syncDatesStrip(strip, sprint, last());
  } catch (err) {
    const strip = liveDatesStrip(owner, sprintId);
    if (!strip) return;
    // Only while the strip still holds the pair that failed — a newer edit reports for itself.
    const shown = field => strip.querySelector(`[data-field="${field}"]`)?.value || null;
    if (shown('start_date') === start && shown('end_date') === end) showDatesError(strip, sprintDateErrorText(err));
  }
}

// ── Sprint start and completion (TPT9102) ──

// Runs one Start or Complete step of this mount, one at a time. The button is left enabled while
// it runs: disabling it would drop keyboard focus, and the dialog gives focus back to its opener.
async function lifecycleStep(step) {
  const owner = state;
  if (owner.lifecycleBusy) return;
  owner.lifecycleBusy = true;
  try {
    await step(owner);
  } finally {
    owner.lifecycleBusy = false;
  }
}

function focusLifecycleAction() {
  document.querySelector('.planning-lifecycle .btn')?.focus();
}

// A refused step means this page's sprint rows are out of date: someone else got there first.
async function reloadSprints(owner) {
  try {
    const { sprints } = await api.get(`/projects/${owner.projectId}/sprints`);
    owner.sprints.splice(0, owner.sprints.length, ...(sprints || []));
  } catch (_) {}
  if (state === owner) renderPlanning();
}

function handleStartSprint() {
  return lifecycleStep(async (owner) => {
    const sprint = getSelectedSprint();
    if (!sprint) return;
    const label = singular();
    try {
      const { sprint: saved } = await api.post(`/projects/${owner.projectId}/sprints/${sprint.id}/start`, {});
      sprint.started_at = saved.started_at;
      if (state !== owner) return;
      renderPlanning();
      focusLifecycleAction();
      showToast(`${label} started`, 'success');
    } catch (err) {
      if (state === owner) showToast(lifecycleErrorText(err, label), 'error');
      if (err.status === 409) await reloadSprints(owner);
    }
  });
}

function carryOverChoice(value, label, { checked = false, disabled = false, hint = '' } = {}) {
  return `<label class="complete-sprint-choice${disabled ? ' complete-sprint-choice--disabled' : ''}">`
    + `<input type="radio" name="unfinished" value="${value}"${checked ? ' checked' : ''}${disabled ? ' disabled' : ''}>`
    + `<span>${escapeHtml(label)}</span>`
    + (hint ? `<span class="complete-sprint-hint">${escapeHtml(hint)}</span>` : '')
    + `</label>`;
}

function completeDialogBody(summary, next) {
  const noun = singular();
  const lower = noun.toLowerCase();
  const finished = summary.total === 0
    ? `This ${lower} has no items.`
    : `${summary.finished} of ${itemCount(summary.total)} finished.`;
  let html = `<form id="complete-sprint-form" class="complete-sprint-form">`;
  html += `<p class="complete-sprint-summary">${escapeHtml(finished)}</p>`;
  if (summary.unfinished > 0) {
    const points = summary.points > 0 ? ` (${summary.points} pts)` : '';
    html += `<fieldset class="complete-sprint-choices">`;
    html += `<legend>Move ${escapeHtml(itemCount(summary.unfinished, 'unfinished'))}${escapeHtml(points)} to</legend>`;
    html += next
      ? carryOverChoice('next', `Next ${lower}: ${selectorLabel(next)}`, { checked: true })
      : carryOverChoice('next', `Next ${lower}`, { disabled: true, hint: `No later open ${lower}` });
    html += carryOverChoice('new', `New ${lower}`);
    html += carryOverChoice('backlog', 'Backlog', { checked: !next });
    html += `</fieldset>`;
  }
  html += `<div class="complete-sprint-error" role="alert" hidden></div>`;
  html += `<div class="complete-sprint-actions">`;
  html += `<button type="submit" class="btn btn-primary" id="complete-sprint-submit">Complete ${escapeHtml(noun)}</button>`;
  html += `<button type="button" class="btn btn-ghost" id="complete-sprint-cancel">Cancel</button>`;
  html += `</div></form>`;
  return html;
}

// Where the tasks went, for the success toast.
function carryOverOutcome(result) {
  if (result.carried_over.length === 0) return '';
  const place = result.target_sprint ? selectorLabel(result.target_sprint) : 'Backlog';
  return ` — ${itemCount(result.carried_over.length)} moved to ${place}`;
}

// Applies a completion to the page: the reply carries the closed row and any sprint it created. The
// unfinished tasks landed in the target, so that is the sprint to look at next.
async function applyCompletion(owner, sprint, result, label) {
  Object.assign(sprint, result.sprint);
  const target = result.target_sprint;
  if (target && !owner.sprints.some(s => s.id === target.id)) owner.sprints.push(target);
  if (state !== owner) return;
  if (target) state.selectedSprintId = target.id;
  showToast(`${label} completed${carryOverOutcome(result)}`, 'success');
  await loadVelocity(owner);
  if (state !== owner) return;
  await refetchTasks();
  if (state === owner) focusLifecycleAction();
}

async function submitCompletion(owner, sprint, overlay, token) {
  const label = singular();
  const chosen = overlay.querySelector('input[name="unfinished"]:checked');
  const errorEl = overlay.querySelector('.complete-sprint-error');
  const submit = overlay.querySelector('#complete-sprint-submit');
  submit.disabled = true;
  errorEl.hidden = true;
  try {
    // With nothing unfinished the API ignores the choice, but it still requires one.
    const unfinished = chosen ? chosen.value : 'backlog';
    const result = await api.post(`/projects/${owner.projectId}/sprints/${sprint.id}/complete`, { unfinished });
    closeModal(token);
    await applyCompletion(owner, sprint, result, label);
  } catch (err) {
    errorEl.textContent = lifecycleErrorText(err, label);
    errorEl.hidden = false;
    submit.disabled = false;
    if (err.status === 409) await reloadSprints(owner);
  }
}

function handleCompleteSprint() {
  return lifecycleStep(async (owner) => {
    const sprint = getSelectedSprint();
    if (!sprint) return;
    // Everything in the sprint, unlike this page's own list: subtasks and epics count too.
    let rows, statuses;
    try {
      const query = `priority=${sprint.number}&fields=board&include_children_count=false&include_active_device=false`;
      [{ tasks: rows }, statuses] = await Promise.all([
        api.get(`/projects/${owner.projectId}/tasks?${query}`),
        loadStatuses(owner.projectId),
      ]);
    } catch (err) {
      if (state === owner) showToast(lifecycleErrorText(err, singular()), 'error');
      return;
    }
    if (state !== owner) return;

    const summary = carryOverSummary(rows, statuses);
    const next = nextOpenSprint(state.sprints, sprint);
    const token = openModal(`Complete ${singular()}`, completeDialogBody(summary, next), {
      onMount(overlay) {
        const form = overlay.querySelector('#complete-sprint-form');
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          submitCompletion(owner, sprint, overlay, token);
        });
        overlay.querySelector('#complete-sprint-cancel').addEventListener('click', () => closeModal(token));
        (overlay.querySelector('input[name="unfinished"]:checked') || overlay.querySelector('#complete-sprint-submit')).focus();
      },
    });
    state.modalToken = token;
  });
}

function saveVelocityOverride(value) {
  setVelocityOverride(value);
  closeModal();
  renderPlanning();
  document.getElementById('planning-velocity-btn')?.focus();
}

function handleVelocityPrompt() {
  const override = getVelocityOverride();
  const { average, count } = state.velocity;
  const known = average > 0;
  const noun = singular().toLowerCase();
  const hint = known
    ? `${averageSourceText(count, singular(), plural())}: ${formatValue(average)} pts. Leave this empty to plan against it.`
    : `No finished ${plural().toLowerCase()} to average yet.`;
  const body = `
    <form id="velocity-form" style="display:flex;flex-direction:column;gap:0.75rem;">
      <label class="form-label" for="velocity-input">Team velocity (story points per ${escapeHtml(noun)})</label>
      <input class="form-input" id="velocity-input" type="number" min="1" aria-describedby="velocity-hint" placeholder="${known ? escapeHtml(formatValue(average)) : 'e.g. 30'}" value="${override || ''}" autofocus>
      <p class="planning-velocity-hint" id="velocity-hint">${escapeHtml(hint)}</p>
      <div style="display:flex;gap:0.5rem;">
        <button type="submit" class="btn btn-primary" style="flex:1;">Save</button>
        ${override ? `<button type="button" class="btn btn-ghost" id="velocity-clear">${known ? 'Use average' : 'Clear'}</button>` : ''}
      </div>
    </form>`;

  state.modalToken = openModal('Set Velocity', body, {
    onMount(overlay) {
      overlay.querySelector('#velocity-form').addEventListener('submit', (e) => {
        e.preventDefault();
        saveVelocityOverride(overlay.querySelector('#velocity-input').value);
      });
      overlay.querySelector('#velocity-clear')?.addEventListener('click', () => saveVelocityOverride(null));
    },
  });
}

// ── Task detail ──

function openTaskDetailPanel(taskKey) {
  state.detailToken = openTaskDetail(state.projectId, taskKey, {
    allTasks: state.tasks,
    onClose: async () => { await refetchTasks(); },
  });
}

// ── Refetch ──

async function refetchTasks() {
  try {
    const { tasks } = await api.get('/projects/' + state.projectId + '/tasks?top_level=true&type=story,task,bug');
    state.tasks.splice(0, state.tasks.length, ...(tasks || []));
  } catch (_) {}
  renderPlanning();
}

// ── Drag and drop ──

let onMouseDown, onMouseMove, onMouseUp;

function bindDragAndDrop() {
  onMouseDown = (e) => {
    const handle = e.target.closest('.planning-drag-handle');
    if (!handle) return;
    const item = handle.closest('.planning-item');
    if (!item) return;

    const rect = item.getBoundingClientRect();
    state.drag = {
      taskKey: item.dataset.taskKey,
      el: item,
      clone: null,
      placeholder: null,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      moved: false,
    };
    e.preventDefault();
  };

  onMouseMove = (e) => {
    const d = state.drag;
    if (!d) return;

    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;

    if (!d.moved) {
      if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
      d.moved = true;

      d.clone = d.el.cloneNode(true);
      d.clone.classList.add('planning-item-dragging');
      d.clone.style.width = d.el.offsetWidth + 'px';
      document.body.appendChild(d.clone);

      d.placeholder = document.createElement('div');
      d.placeholder.className = 'planning-item-placeholder';
      d.placeholder.style.height = d.el.offsetHeight + 'px';
      d.el.parentNode.insertBefore(d.placeholder, d.el);

      d.el.classList.add('planning-item-hidden');
    }

    d.clone.style.left = (e.clientX - d.offsetX) + 'px';
    d.clone.style.top = (e.clientY - d.offsetY) + 'px';

    const target = document.elementFromPoint(e.clientX, e.clientY);
    if (!target) return;
    const container = target.closest('.planning-items');

    document.querySelectorAll('.planning-items.drag-over')
      .forEach(el => el.classList.remove('drag-over'));

    if (!container) return;
    container.classList.add('drag-over');

    const items = [...container.querySelectorAll('.planning-item:not(.planning-item-hidden)')];
    const insertBefore = findInsertBefore(items, e.clientX, e.clientY, 'y');

    if (insertBefore) {
      container.insertBefore(d.placeholder, insertBefore);
    } else {
      container.appendChild(d.placeholder);
    }
  };

  onMouseUp = async (e) => {
    const d = state.drag;
    if (!d) return;

    document.querySelectorAll('.planning-items.drag-over')
      .forEach(el => el.classList.remove('drag-over'));

    if (!d.moved) {
      state.drag = null;
      return;
    }

    const container = d.placeholder?.closest('.planning-items');
    const panel = container ? container.dataset.panel : null;

    // Must run BEFORE the cleanup below removes 'planning-item-hidden' from d.el — dropIndex()
    // counts elements NOT carrying that class, so a hidden-then-unhidden dragged original
    // would otherwise start counting itself (see lib/drag-order.js).
    const placeholderIndex = container
      ? dropIndex(container, d.placeholder, { selector: '.planning-item', hiddenClass: 'planning-item-hidden' })
      : 0;

    // Cleanup DOM
    if (d.clone) d.clone.remove();
    if (d.placeholder) d.placeholder.remove();
    d.el.classList.remove('planning-item-hidden');

    const task = state.tasks.find(t => t.task_key === d.taskKey);
    state.drag = null;

    if (!task || !panel) return;

    // Determine target priority
    const sprint = getSelectedSprint();
    let targetPriority;
    if (panel === 'sprint') {
      if (!sprint) return;
      targetPriority = sprint.number;
    } else {
      targetPriority = 0;
    }

    // Build new ordered list for target
    const laneTasks = state.tasks
      .filter(t => t.priority === targetPriority && t.task_key !== task.task_key)
      .sort((a, b) => b.display_order - a.display_order);

    const insertAt = Math.min(placeholderIndex, laneTasks.length);
    laneTasks.splice(insertAt, 0, task);

    // Calculate patches
    const patches = [];
    laneTasks.forEach((t, i) => {
      const newOrder = (laneTasks.length - i) * 10;
      const priorityChanged = t.task_key === task.task_key && t.priority !== targetPriority;
      const orderChanged = t.display_order !== newOrder;
      if (priorityChanged || orderChanged) {
        const patch = { display_order: newOrder };
        if (priorityChanged) patch.priority = targetPriority;
        patches.push({ taskKey: t.task_key, patch });
      }
    });

    if (patches.length === 0) return;

    // Optimistic update
    task.priority = targetPriority;
    laneTasks.forEach((t, i) => { t.display_order = (laneTasks.length - i) * 10; });
    renderPlanning();

    try {
      const results = await Promise.all(patches.map(({ taskKey, patch }) =>
        api.patch(`/projects/${state.projectId}/tasks/${taskKey}`, patch)
      ));
      // C1464 — dropping an objective parent into a sprint can cascade its children
      // (and its own priority) to sprints beyond this drag's own optimistic update —
      // refetch so the board reflects the server-computed schedule instead of going stale.
      if (results.some(r => r && r.children_rescheduled)) {
        await refetchTasks();
      }
    } catch (err) {
      showToast('Failed to save', 'error');
      await refetchTasks();
    }
  };

  document.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
}

function cleanupDrag() {
  if (state.drag) {
    if (state.drag.clone) state.drag.clone.remove();
    if (state.drag.placeholder) state.drag.placeholder.remove();
    if (state.drag.el) state.drag.el.classList.remove('planning-item-hidden');
    state.drag = null;
  }
  document.querySelectorAll('.planning-items.drag-over')
    .forEach(el => el.classList.remove('drag-over'));
  if (onMouseDown) document.removeEventListener('mousedown', onMouseDown);
  if (onMouseMove) document.removeEventListener('mousemove', onMouseMove);
  if (onMouseUp) document.removeEventListener('mouseup', onMouseUp);
}

// ── Events ──

let containerHandler = null;
let searchHandler = null;

function bindEvents() {
  const container = document.getElementById('planning-container');
  if (!container) return;

  containerHandler = (e) => {
    // Sprint selector
    if (e.type === 'change' && e.target.id === 'sprint-selector') {
      handleSprintSelect(e);
      return;
    }

    // Sprint Start/End dates (TPT589)
    if (e.target.classList && e.target.classList.contains('planning-date-input')) {
      const strip = e.target.closest('.planning-dates');
      if (!strip) return;
      if (e.type === 'change') {
        queueSprintDatesSave(strip);
      } else if (e.type === 'focusout' && dateInputValue(e.target) === undefined) {
        // A date left half-typed goes back to the saved one.
        const sprint = state.sprints.find(s => s.id === Number(strip.dataset.sprintId));
        if (sprint) e.target.value = sprint[e.target.dataset.field] || '';
      }
      return;
    }

    if (e.type === 'click') {
      // Sprint start and completion (TPT9102)
      if (e.target.closest('#planning-start-sprint')) {
        handleStartSprint();
        return;
      }
      if (e.target.closest('#planning-complete-sprint')) {
        handleCompleteSprint();
        return;
      }

      // Velocity button
      if (e.target.closest('#planning-velocity-btn')) {
        handleVelocityPrompt();
        return;
      }

      // Goal click → inline edit
      const goalEl = e.target.closest('#planning-goal');
      if (goalEl && !goalEl.querySelector('input')) {
        const sprint = getSelectedSprint();
        const currentGoal = sprint ? (sprint.goal || '') : '';
        goalEl.innerHTML = `<input class="form-input planning-goal-input" id="planning-goal-input" value="${escapeHtml(currentGoal)}" placeholder="${escapeHtml(singular())} goal...">`;
        const input = goalEl.querySelector('#planning-goal-input');
        input.focus();
        input.addEventListener('blur', () => { handleGoalSave(input.value.trim()); });
        input.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter') { ev.preventDefault(); input.blur(); }
          if (ev.key === 'Escape') { renderPlanning(); }
        });
        return;
      }

      // Item click → task detail (exclude drag handle)
      const item = e.target.closest('.planning-item');
      if (item && !e.target.closest('.planning-drag-handle, a')) {
        openTaskDetailPanel(item.dataset.taskKey);
        return;
      }
    }
  };

  container.addEventListener('click', containerHandler);
  container.addEventListener('change', containerHandler);
  container.addEventListener('focusout', containerHandler);

  // Debounced search
  searchHandler = debounce((e) => {
    state.searchQuery = e.target.value.trim();
    // Re-render left panel only
    const leftItems = document.getElementById('backlog-items');
    const leftHeader = document.querySelector('#planning-left .planning-count');
    const leftFooter = document.querySelector('#planning-left .planning-footer');
    if (!leftItems) return;

    const tasks = getBacklogTasks();
    const pts = sumPoints(tasks);

    if (tasks.length === 0) {
      leftItems.innerHTML = `<div class="empty text-muted" style="padding:1rem;font-size:0.85rem;">${state.searchQuery ? 'No matching items' : 'No backlog items'}</div>`;
    } else {
      leftItems.innerHTML = tasks.map(renderPlanningItem).join('');
    }
    if (leftHeader) leftHeader.textContent = `${tasks.length} item${tasks.length !== 1 ? 's' : ''}`;
    if (leftFooter) leftFooter.innerHTML = `<span>${pts} pts</span>`;
  }, 200);

  container.addEventListener('input', (e) => {
    if (e.target.id === 'planning-search') searchHandler(e);
  });
}

function cleanupEvents() {
  const container = document.getElementById('planning-container');
  if (container && containerHandler) {
    container.removeEventListener('click', containerHandler);
    container.removeEventListener('change', containerHandler);
    container.removeEventListener('focusout', containerHandler);
  }
  containerHandler = null;
  searchHandler = null;
}

// ── Page entry ──

export async function sprintPlanningPage(params) {
  // Read before the first await: once it changes, the user navigated on and this mount stops
  // after its next await (see router.js getNavigationId()).
  const navId = getNavigationId();
  const superseded = () => getNavigationId() !== navId;
  if (!(await checkAuth()) || superseded()) return;
  resetState();
  const owner = state;
  state.projectId = params.id;

  initLayout(getUser());
  // viewLabel set generically here — the project fetch below hasn't resolved yet
  // (C1395, same ordering board.js already lives with for its header button).
  updateLayoutContext({ projectId: params.id, projectName: null, activeView: 'planning', viewLabel: 'Sprint Planning' });

  const content = getContentEl();
  content.innerHTML = `
    <div class="page-header">
      <h2 id="planning-title">Sprint Planning</h2>
    </div>
    <div id="planning-container"><div class="empty"><span class="spinner"></span></div></div>`;

  try {
    const [tasksRes, sprintsRes, project] = await Promise.all([
      api.get('/projects/' + params.id + '/tasks?top_level=true&type=story,task,bug'),
      api.get('/projects/' + params.id + '/sprints'),
      // (C1395) task_group_label for every noun on this page — own fail-open leg
      // (loadProject already fails open to null), same shape as board.js's leg.
      loadProject(params.id),
      // (TPT9103) Fail-open too: without it the capacity indicator offers "Set velocity".
      loadVelocity(owner),
    ]);
    if (superseded()) return;
    state.tasks = tasksRes.tasks || [];
    state.sprints = sprintsRes.sprints || [];
    state.project = project;

    const titleEl = document.getElementById('planning-title');
    if (titleEl) titleEl.textContent = `${plural()} Planning`;
    updateLayoutContext({ projectId: params.id, projectName: project?.name || null, activeView: 'planning', viewLabel: `${plural()} Planning` });

    // Auto-select first active sprint
    const active = state.sprints.find(s => s.status === 'active');
    if (active) state.selectedSprintId = active.id;

    renderPlanning();
    bindEvents();
    bindDragAndDrop();
  } catch (err) {
    if (superseded()) return;
    document.getElementById('planning-container').innerHTML =
      `<div class="empty text-muted">Failed to load planning data</div>`;
  }

  return () => {
    // A late teardown (router.js runs it as soon as it resolves) must leave a newer mount of
    // this page alone: that mount owns the module state by then.
    if (state !== owner) return;
    closeTaskDetail(state.detailToken);
    cleanupDrag();
    cleanupEvents();
    closeModal(state.modalToken);
    resetState();
  };
}
